<template>
  <div>
    <!-- Encabezado con selector de tipo de documento -->
    <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
      <div class="flex items-center">
        <button 
          class="mr-3 p-2.5 text-white bg-teal-800 hover:bg-teal-900 rounded-lg transition-colors" 
          @click="back()"
          title="Volver atrás"
        >
          <i class="fa fa-arrow-left"></i>
        </button>
        <div>
          <h1 class="text-3xl font-bold text-gray-900">
            {{ form.type === 'invoice' ? 'Crear Factura' : 'Crear Nota de Entrega' }}
          </h1>
          <p class="text-sm text-gray-500">
            {{ form.type === 'invoice' ? 'Emisión de factura comercial' : 'Emisión de nota de entrega para despacho' }}
          </p>
        </div>
      </div>

      <!-- Toggle selector de tipo de documento -->
      <div class="flex items-center p-1 bg-gray-200 rounded-xl shadow-inner">
        <button
          type="button"
          @click="form.type = 'delivery_note'"
          :class="[
            'px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 transition-all',
            form.type === 'delivery_note'
              ? 'bg-white text-teal-800 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          ]"
        >
          <i class="fa fa-file-lines"></i>
          Nota de entrega
        </button>

        <button
          type="button"
          @click="form.type = 'invoice'"
          :class="[
            'px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 transition-all',
            form.type === 'invoice'
              ? 'bg-white text-blue-700 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          ]"
        >
          <i class="fa fa-file-invoice"></i>
          Factura
        </button>
      </div>
    </div>

    <Head :title="form.type === 'invoice' ? 'Crear Factura' : 'Crear Nota de Entrega'" />

    <!-- Sección de cliente -->
    <div class="bg-white rounded-lg shadow-md overflow-hidden mb-6">
      <div class="p-6">
        <find-or-create-client @addClient="takeClient" />
        
        <!-- Formulario de cliente -->
        <div v-if="showClientCreate" class="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
          <text-input 
            v-model="form.client.name"
            label="Nombre y Apellido"
            placeholder="Ingrese el nombre completo"
          />

          <text-input
            v-model="form.client.identification" 
            label="Alias o Cédula"
            placeholder="Ingrese identificación"
          />

          <text-input
            v-model="form.client.phone"
            label="Teléfono"
            placeholder="Ingrese número de contacto" 
          />

          <text-input
            v-model="form.client.address"
            label="Dirección"
            placeholder="Ingrese dirección completa"
          />

          <text-input
            v-model="form.client.balance"
            label="Monto adeudado" 
            disabled
          />

          <div>
            <label class="block text-gray-700 mb-2">Tipo de cliente</label>
            <select 
              v-model="form.client.typeClient"
              class="form-select w-full rounded-lg border-gray-300"
            >
              <option value="">Seleccionar tipo</option>
              <option value="freelancer">Freelancer</option>
              <option value="spot">SPOT</option>
              <option value="a">A</option>
              <option value="b">B</option>
              <option value="c">C</option>
            </select>
          </div>
        </div>
      </div>
    </div>

    <!-- Sección de productos -->
    <div class="bg-white rounded-lg shadow-md overflow-hidden mb-6">
      <div class="p-6">
        <div class="flex flex-col sm:flex-row items-center justify-between mb-6 gap-4">
          <div class="flex gap-2">
            <button 
              class="btn-blue flex items-center"
              @click="listProducts = !listProducts"
              title="Ver lista de productos"
            >
              <i class="fa fa-list mr-2"></i>
              Lista
            </button>

            <button 
              class="btn-blue flex items-center"
              @click="addQuickProduct()"
              title="Agregar producto rápido"
            >
              <i class="fa fa-plus mr-2"></i>
              Producto rápido
            </button>
          </div>

          <search-filter
            v-model="form.search.terms"
            class="w-full sm:max-w-md"
            @reset="form.search.terms = ''"
          >
          </search-filter>
        </div>

        <!-- Lista de productos -->
        <div v-if="listProducts" class="mb-6">
          <div class="overflow-x-auto rounded-lg border">
            <table class="w-full">
              <thead class="bg-gray-50">
                <tr>
                  <th class="p-3 text-left">Código</th>
                  <th class="p-3 text-left">Producto</th>
                  <th class="p-3 text-center">Disponibles</th>
                  <th 
                    v-for="group in groups" 
                    :key="group.id" 
                    class="p-3 text-right"
                  >
                    Precio {{ group.name }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr 
                  v-for="product in products.data" 
                  :key="product.id"
                  class="hover:bg-gray-50 cursor-pointer border-t"
                  @click="selectProduct(product)"
                >
                  <td class="p-3">{{ product.code }}</td>
                  <td class="p-3">{{ product.name }}</td>
                  <td class="p-3 text-center">{{ product.quantity }}</td>
                  <td 
                    v-for="group in groups" 
                    :key="group.id" 
                    class="p-3 text-right"
                  >
                    ${{ getGroupPrice(product.prices, group.id) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Carrito de compras -->
        <div class="overflow-x-auto rounded-lg border">
          <table class="w-full">
            <thead class="bg-gray-50">
              <tr>
                <th class="p-3 text-left">Código</th>
                <th class="p-3 text-left">Producto</th>
                <th class="p-3 text-center">Disponible</th>
                <th class="p-3 text-center">Cantidad</th>
                <th class="p-3 text-right">Precio</th>
                <th class="p-3 text-right">Total</th>
                <th class="p-3"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(product, index) in form.products" :key="product.id" class="border-t">
                <td class="p-3">
                  <text-input 
                    v-if="product.quick"
                    v-model="form.products[index].code"
                    placeholder="Código"
                  />
                  <span v-else>{{ product.code }}</span>
                </td>
                <td class="p-3">
                  <text-input
                    v-if="product.quick"
                    v-model="form.products[index].name"
                    placeholder="Nombre"
                  />
                  <div v-else>
                    <span>{{ product.name }}</span>
                    <div v-if="product.require_note || isCustomBsProduct(product.code) || isPlanProduct(product.code)" class="mt-2">
                      <input 
                        type="text" 
                        v-model="form.products[index].note" 
                        class="w-full p-2 border border-gray-300 rounded text-sm bg-gray-50 focus:bg-white" 
                        :placeholder="isCustomBsProduct(product.code) ? 'Número a recargar *' : (isPlanProduct(product.code) ? 'Nombre del Plan / Nota *' : 'Nota requerida *')"
                        required
                      />
                    </div>
                  </div>
                </td>
                <td class="p-3 text-center">{{ product.stock }}</td>
                <td class="p-3">
                  <text-input
                    v-model="form.products[index].quantity"
                    type="number"
                    :max="product.stock"
                    class="w-20 text-center"
                  />
                </td>
                <td class="p-3">
                  <!-- Caso 1: Planes -->
                  <div v-if="isPlanProduct(product.code)" class="space-y-1">
                    <div class="flex items-center justify-between mb-1">
                      <span class="text-[10px] text-gray-500 font-medium">Plan</span>
                      <button 
                        type="button" 
                        @click="openPlanConfigModal(product, index)"
                        class="text-[10px] text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1 transition-colors cursor-pointer hover:underline"
                        title="Administrar Planes"
                      >
                        <i class="fa fa-cog text-[9px]"></i>
                        <span>Administrar</span>
                      </button>
                    </div>
                    <select 
                      v-model="form.products[index].selected_plan"
                      @change="onPlanChange(index)"
                      class="w-full rounded-md border-gray-300 text-xs py-1 px-1.5 bg-white font-medium mb-1 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option 
                        v-for="plan in getProductPlans(product)" 
                        :key="plan.name" 
                        :value="plan.name"
                      >
                        {{ plan.label }}
                      </option>
                      <option value="__config__" class="font-medium text-blue-600 bg-blue-50">
                        ⚙️ Administrar planes...
                      </option>
                    </select>
                    <div class="flex items-center gap-1">
                      <span class="text-xs font-bold text-blue-700">Bs.</span>
                      <input 
                        type="number"
                        step="any"
                        min="0"
                        v-model="form.products[index].price_bs"
                        @input="onPriceBsChange(index)"
                        placeholder="0.00"
                        class="w-full text-right rounded border-blue-300 py-1 px-1.5 text-xs font-bold text-blue-900 bg-blue-50/40"
                      />
                    </div>
                    <div class="text-[11px] text-gray-500 text-right">
                      ≈ ${{ Number(form.products[index].price || 0).toFixed(2) }}
                    </div>
                  </div>

                  <!-- Caso 2: Monto en Bs -->
                  <div v-else-if="isCustomBsProduct(product.code)" class="space-y-1">
                    <div class="flex items-center gap-1">
                      <span class="text-xs font-bold text-emerald-700">Bs.</span>
                      <input 
                        type="number"
                        step="any"
                        min="0"
                        v-model="form.products[index].price_bs"
                        @input="onPriceBsChange(index)"
                        placeholder="0.00"
                        class="w-full text-right rounded border-emerald-300 py-1 px-1.5 text-sm font-bold text-emerald-900 bg-emerald-50/40"
                      />
                    </div>
                    <div class="text-[11px] text-gray-500 text-right">
                      ≈ ${{ Number(form.products[index].price || 0).toFixed(2) }}
                    </div>
                  </div>

                  <!-- Caso 3: Normal -->
                  <div v-else>
                    <select 
                      v-model="form.products[index].priceType"
                      @change="onPriceTypeChange(index)"
                      class="w-full rounded-lg border-gray-300 mb-2"
                    >
                      <option 
                        v-for="group in groups" 
                        :key="group.id" 
                        :value="'group-'+group.id"
                      >
                        {{ group.name }} - ${{ getGroupPrice(product.prices, group.id) }}
                      </option>
                      <option v-if="auth.user.role !== 'sales'" value="custom">Personalizado</option>
                    </select>

                    <text-input
                      v-model="form.products[index].price"
                      type="number"
                      :disabled="form.products[index].priceType !== 'custom' || (!product.quick && auth.user.role == 'sales')"
                      class="w-24 text-right"
                    />
                  </div>
                </td>
                <td class="p-3 text-right">
                  ${{ (form.products[index].price * product.quantity).toFixed(2) }}
                </td>
                <td class="p-3 text-center">
                  <button
                    @click="removeProduct(product)"
                    class="p-2 text-white bg-red-500 hover:bg-red-600 rounded-full transition-colors"
                    title="Eliminar producto"
                  >
                    <i class="fa fa-trash"></i>
                  </button>
                </td>
              </tr>
            </tbody>
            <tfoot class="bg-gray-50">
              <tr>
                <td colspan="5" class="p-3 text-right">Subtotal:</td>
                <td class="p-3 text-right">
                  <div class="font-medium">${{ getSubtotal() }}</div>
                </td>
                <td></td>
              </tr>
              <tr v-if="getDiscountAmount() > 0">
                <td colspan="5" class="p-3 text-right text-red-600">
                  Descuento 
                  <span v-if="form.discount.type === 'percentage'">({{ form.discount.value }}%)</span>:
                </td>
                <td class="p-3 text-right text-red-600">
                  <div>-${{ getDiscountAmount() }}</div>
                </td>
                <td></td>
              </tr>
              <tr>
                <td colspan="5" class="p-3 text-right font-bold">Total:</td>
                <td class="p-3 text-right">
                  <div class="text-xl font-bold">${{ getTotal() }}</div>
                  <div class="text-sm text-gray-600">Bs. {{ (getTotal() * form.change).toFixed(2) }}</div>
                </td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>

    <!-- Sección de descuento -->
    <div class="bg-white rounded-lg shadow-md overflow-hidden mb-6">
      <div class="p-6">
        <h3 class="text-lg font-semibold mb-4">Aplicar descuento</h3>
        
        <div class="flex flex-wrap items-end gap-4">
          <div>
            <label class="block text-gray-700 mb-2">Tipo de descuento</label>
            <select 
              v-model="form.discount.type"
              class="form-select rounded-lg border-gray-300"
            >
              <option value="percentage">Porcentaje (%)</option>
              <option value="amount">Monto fijo ($)</option>
            </select>
          </div>
          
          <text-input
            v-model="form.discount.value"
            :label="form.discount.type === 'percentage' ? 'Porcentaje (%)' : 'Monto ($)'"
            type="number"
            :step="form.discount.type === 'percentage' ? '1' : '0.01'"
            :min="0"
            :max="form.discount.type === 'percentage' ? 100 : null"
            class="w-32"
          />
          
          <div class="text-gray-600">
            <span class="font-medium">Descuento aplicado:</span> 
            <span class="text-red-600 font-bold">${{ getDiscountAmount() }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Opciones de crédito -->
    <div class="bg-blue-100 rounded-lg p-4 mb-6">
      <label class="inline-flex items-center cursor-pointer">
        <input 
          v-model="form.isCredit"
          type="checkbox"
          class="form-checkbox h-5 w-5 text-blue-600"
        >
        <span class="ml-2 text-lg font-semibold">Venta a crédito</span>
      </label>
    </div>

    <!-- Detalles adicionales -->
    <div class="bg-white rounded-lg shadow-md overflow-hidden mb-6">
      <div class="p-6 space-y-6">
        <!-- Campo para ingresar el número correlativo de la orden -->
        <!-- Campo para ingresar el número correlativo de la orden -->
        <text-input
          v-model="form.number_order"
          label="Correlativo de Pedido / Nota de Entrega (Opcional)"
          :placeholder="`Automático (Siguiente: ${nextNumberOrder || 'automático'})`"
        />

        <!-- Campo para ingresar el número / correlativo de la factura -->
        <div v-if="form.type === 'invoice'" class="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <text-input
            v-model="form.invoice_number"
            label="Número / Correlativo de Factura (Opcional)"
            :placeholder="`Automático (Siguiente: ${nextInvoiceNumber || 'automático'})`"
          />
          <p class="text-xs text-blue-600 mt-1">
            <i class="fa fa-info-circle mr-1"></i>
            Si deja este campo vacío, se asignará automáticamente el correlativo ({{ nextInvoiceNumber || 'automático' }}).
          </p>
        </div>

        <text-input
          v-model="form.date"
          label="Fecha y hora"
          type="datetime-local"
          required
        />

        <text-input
          v-model="form.change"
          label="Tasa de cambio"
          type="number"
          step="0.01"
        />

        <text-area-input
          v-model="form.comentary"
          label="Observaciones"
          placeholder="Ingrese observaciones importantes"
          required
        />
      </div>
    </div>

    <!-- Botón de generar venta / nota de entrega / factura -->
    <div class="flex justify-end">
      <button 
        @click="generateOrder"
        :class="[
          'text-lg px-8 py-3 rounded-lg font-bold text-white transition-all shadow-md flex items-center',
          form.type === 'invoice' ? 'bg-blue-700 hover:bg-blue-800' : 'bg-teal-700 hover:bg-teal-800'
        ]"
      >
        <i class="fa fa-check mr-2"></i>
        {{ form.type === 'invoice' ? 'Generar Factura' : 'Generar Nota de Entrega' }}
      </button>
    </div>

    <!-- Modal para configurar planes del producto -->
    <div v-if="showPlanModal" class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div class="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden border border-gray-100">
        <!-- Header -->
        <div class="bg-blue-600 text-white px-5 py-4 flex items-center justify-between">
          <div class="flex items-center gap-2.5">
            <i class="fa fa-cogs text-lg"></i>
            <div>
              <h3 class="font-bold text-base leading-tight text-white">Administrar Planes</h3>
              <p class="text-xs text-blue-100 font-normal mt-0.5">{{ editingPlanProduct?.name }}</p>
            </div>
          </div>
          <button 
            type="button" 
            @click="closePlanModal" 
            class="text-white hover:text-white/80 p-1.5 rounded-lg hover:bg-black/10 transition-colors flex items-center justify-center cursor-pointer"
            title="Cerrar"
          >
            <i class="fa fa-times text-lg"></i>
          </button>
        </div>

        <!-- Body -->
        <div class="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
          <p class="text-xs text-blue-900 bg-blue-50 p-3 rounded-lg border border-blue-200 leading-relaxed">
            Aquí puedes <strong>agregar nuevos planes</strong>, <strong>modificar tarifas en Bolívares (Bs.)</strong> o <strong>eliminar planes</strong>. Los cambios se guardarán de inmediato para este producto.
          </p>

          <div class="space-y-3">
            <div 
              v-for="(plan, idx) in editingPlans" 
              :key="idx" 
              class="bg-white p-3 rounded-lg border border-gray-200 shadow-sm space-y-2 hover:border-blue-300 transition-colors"
            >
              <div class="flex items-center gap-2">
                <div class="flex-1">
                  <label class="block text-[11px] font-semibold text-gray-600 mb-0.5">Nombre del Plan</label>
                  <input 
                    type="text" 
                    v-model="plan.name" 
                    placeholder="Ej: Plan 6GB" 
                    class="w-full text-xs font-semibold border-gray-300 rounded px-2.5 py-1.5 focus:ring-blue-500 focus:border-blue-500 bg-gray-50/50"
                  />
                </div>
                <div class="w-36">
                  <label class="block text-[11px] font-semibold text-gray-600 mb-0.5">Tarifa en Bs.</label>
                  <div class="flex items-center gap-1">
                    <span class="text-xs font-bold text-blue-700">Bs.</span>
                    <input 
                      type="number" 
                      step="any" 
                      min="0" 
                      v-model.number="plan.price" 
                      placeholder="0.00" 
                      class="w-full text-xs font-bold text-right border-gray-300 rounded px-2 py-1.5 focus:ring-blue-500 focus:border-blue-500 text-blue-900 bg-gray-50/50"
                    />
                  </div>
                </div>
                <div class="pt-4 flex justify-center">
                  <button 
                    type="button" 
                    @click="removePlanRow(idx)" 
                    class="text-red-400 hover:text-red-600 p-1.5 rounded hover:bg-red-50 transition-colors cursor-pointer"
                    title="Eliminar este plan"
                  >
                    <i class="fa fa-trash-alt text-xs"></i>
                  </button>
                </div>
              </div>
              <div class="flex items-center justify-between text-[11px] text-gray-500 pt-1 border-t border-gray-100">
                <span>Equivalente referencial:</span>
                <span class="font-semibold text-gray-700">
                  ${{ (change > 0 && plan.price > 0) ? (Number(plan.price) / Number(change)).toFixed(2) : '0.00' }} USD
                </span>
              </div>
            </div>
          </div>

          <button 
            type="button" 
            @click="addPlanRow" 
            class="w-full py-2.5 border-2 border-dashed border-blue-300 hover:border-blue-500 bg-blue-50/30 hover:bg-blue-50 text-blue-700 text-xs font-bold rounded-lg transition-colors flex items-center justify-center gap-1.5 cursor-pointer shadow-sm"
          >
            <i class="fa fa-plus-circle text-sm text-blue-600"></i>
            <span>+ Añadir Nuevo Plan</span>
          </button>
        </div>

        <!-- Footer -->
        <div class="bg-gray-50 px-5 py-3 border-t flex justify-end gap-2">
          <button 
            type="button" 
            @click="closePlanModal" 
            class="px-4 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Cancelar
          </button>
          <button 
            type="button" 
            @click="savePlansConfig" 
            :disabled="savingPlans" 
            class="px-4 py-2 text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-sm flex items-center gap-1.5 transition-colors disabled:opacity-50"
          >
            <i v-if="savingPlans" class="fa fa-spinner fa-spin"></i>
            <span>{{ savingPlans ? 'Guardando...' : 'Guardar Cambios' }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import Layout from '../../Shared/Layout.vue'
import TextInput from '@/Shared/TextInput'
import pickBy from 'lodash/pickBy'
import { Head } from '@inertiajs/inertia-vue3'
import SelectInput from '@/Shared/SelectInput.vue'
import FindOrCreateClient from '../../Shared/FindOrCreateClient.vue'
import TextAreaInput from '@/Shared/TextAreaInput'
import SearchFilter from '@/Shared/SearchFilter.vue'

export default {
  components: {
    SelectInput,
    TextInput,
    Head,
    FindOrCreateClient,
    TextAreaInput,
    SearchFilter
  },

  layout: Layout,

  props: {
    products: Object,
    groups: Object,
    client: Object,
    brands: Object,
    parts: Object,
    models: Object,
    deposits: Object,
    regions: Object,
    change: Number,
    auth: Object,
    documentType: {
      type: String,
      default: 'delivery_note'
    },
    nextNumberOrder: String,
    nextInvoiceNumber: String
  },

  data() {
    return {
      getQuantity(inventory) {
        return inventory.reduce((total, inv) => total + inv.quantity, 0)
      },

      getTotal() {
        return this.form.products.reduce((total, product) => {
          return total + (product.price * product.quantity)
        }, 0).toFixed(2)
      },

      selected: null,
      options: ['list', 'of', 'options'],
      
      form: this.$inertia.form({
        type: this.documentType || 'delivery_note',
        invoice_number: '',
        date: '',
        search: {
          terms: '',
          deposit: '',
          brand_id: '',
          part_id: '',
          truck_model_id: '',
          region_id: '',
        },
        isCredit: false,
        number_order: null,
        client: {
          name: '',
          email: '',
          cedula: '',
          telefono: '',
          address: '',
          identification: '',
          balance: 0,
          typeClient: '',
        },
        discount: {
          type: 'percentage', // 'percentage' or 'amount'
          value: 0
        },
        comentary: '',
        products: [],
        method: '',
        referencePay: '',
        change: this.change,
        estimate: true,
        order_buy: ''
      }),

      listProducts: false,
      showClientCreate: false,
      textSearch: 'Buscar cliente',
      showPlanModal: false,
      editingPlanProduct: null,
      editingPlanIndex: null,
      editingPlans: [],
      savingPlans: false,
    }
  },

  watch: {
    'form.search.terms': 'onSearchProductsChange',
    'form.search.deposit': 'onSearchProductsChange', 
    'form.search.brand_id': 'onSearchProductsChange',
    'form.search.part_id': 'onSearchProductsChange',
    'form.search.truck_model_id': 'onSearchProductsChange',
    'form.search.region_id': 'onSearchProductsChange',
    'form.change'(newVal) {
      if (newVal > 0 && this.form.products) {
        this.form.products.forEach((_, idx) => {
          if (this.form.products[idx].price_bs) {
            this.onPriceBsChange(idx)
          }
        })
      }
    }
  },

  mounted() {
    this.form.date = new Date().toISOString().substr(0,10)
  },

  methods: {
    getGroupPrice(prices, groupId) {
      const match = prices.find(p => p.group_id === groupId)
      return match ? match.price : '-'
    },
    takeClient(client) {
      this.form.client = client
    },

    getDeposits(deposits) {
      return deposits
    },

    getSubtotal() {
      return this.form.products.reduce((total, product) => {
        return total + (parseFloat(product.price) * parseFloat(product.quantity))
      }, 0).toFixed(2)
    },

    getDiscountAmount() {
      const subtotal = parseFloat(this.getSubtotal());
      if (this.form.discount.type === 'percentage') {
        return ((subtotal * parseFloat(this.form.discount.value)) / 100).toFixed(2);
      } else {
        return Math.min(parseFloat(this.form.discount.value), subtotal).toFixed(2);
      }
    },

    getTotal() {
      const subtotal = parseFloat(this.getSubtotal());
      const discount = parseFloat(this.getDiscountAmount());
      return (subtotal - discount).toFixed(2);
    },

    back() {
      window.history.back()
    },

    addQuickProduct() {
      this.form.products.push({
        id: 'quick',
        code: '',
        name: '',
        deposit: '',
        quantity: 1,
        price: 0,
        quick: true
      })
    },

    getProductStock(inventory, deposito) {
      const found = inventory.find(inv => inv.deposit.id == deposito)
      return found ? found.quantity : ''
    },

    cleanCode(code) {
      return (code || '').replace(/[\s\-_.]/g, '').toUpperCase()
    },

    isPlanProduct(code) {
      const c = this.cleanCode(code)
      return c === 'RECARGAPLANES' || c === 'RECARGA2'
    },

    isCustomBsProduct(code) {
      const c = this.cleanCode(code)
      return c === 'RECARGAVTA' || c === 'RECARGAREEMPLAZO'
    },

    getProductPlans(product) {
      const code = typeof product === 'string' ? product : product?.code
      const c = this.cleanCode(code)

      // 1. Revisar si el producto tiene planes personalizados guardados en description (JSON)
      if (typeof product === 'object' && product?.description) {
        try {
          const parsed = typeof product.description === 'string' ? JSON.parse(product.description) : product.description
          if (Array.isArray(parsed) && parsed.length > 0 && parsed[0].name) {
            return [
              ...parsed.map((p) => ({
                name: p.name,
                price: Number(p.price || 0),
                label: `${p.name} - ${Number(p.price || 0).toLocaleString('es-VE')} Bs.`
              })),
              { name: 'Personalizado', price: 0, label: 'Otro monto (Personalizado en Bs.)' }
            ]
          }
        } catch (e) {}
      }

      // 2. Precios por grupo configurados
      const prices = (typeof product === 'object' && product?.prices) ? product.prices : []
      const p1 = (prices && prices[0] && parseFloat(prices[0].price) > 0) ? parseFloat(prices[0].price) : null
      const p2 = (prices && prices[1] && parseFloat(prices[1].price) > 0) ? parseFloat(prices[1].price) : null
      const p3 = (prices && prices[2] && parseFloat(prices[2].price) > 0) ? parseFloat(prices[2].price) : null

      if (c === 'RECARGAPLANES') {
        const price1 = p1 !== null ? p1 : 5500
        const price2 = p2 !== null ? p2 : 10000
        const price3 = p3 !== null ? p3 : 20000
        return [
          { name: 'Plan 6GB', price: price1, label: `Plan 6GB - ${Number(price1).toLocaleString('es-VE')} Bs.` },
          { name: 'Plan 12GB', price: price2, label: `Plan 12GB - ${Number(price2).toLocaleString('es-VE')} Bs.` },
          { name: 'Plan 30GB', price: price3, label: `Plan 30GB - ${Number(price3).toLocaleString('es-VE')} Bs.` },
          { name: 'Personalizado', price: 0, label: 'Otro monto (Personalizado en Bs.)' },
        ]
      }
      if (c === 'RECARGA2') {
        const price1 = p1 !== null ? p1 : 300
        const price2 = p2 !== null ? p2 : 500
        const price3 = p3 !== null ? p3 : 1000
        return [
          { name: 'Plan 6GB', price: price1, label: `Plan 6GB - ${Number(price1).toLocaleString('es-VE')} Bs.` },
          { name: 'Plan 12GB', price: price2, label: `Plan 12GB - ${Number(price2).toLocaleString('es-VE')} Bs.` },
          { name: 'Plan 30GB', price: price3, label: `Plan 30GB - ${Number(price3).toLocaleString('es-VE')} Bs.` },
          { name: 'Personalizado', price: 0, label: 'Otro monto (Personalizado en Bs.)' },
        ]
      }
      return []
    },

    openPlanConfigModal(product, index) {
      this.editingPlanProduct = product
      this.editingPlanIndex = index
      const currentPlans = this.getProductPlans(product).filter(p => p.name !== 'Personalizado')
      this.editingPlans = currentPlans.map(p => ({
        name: p.name,
        price: p.price
      }))
      if (this.editingPlans.length === 0) {
        this.editingPlans = [
          { name: 'Plan 6GB', price: 5500 },
          { name: 'Plan 12GB', price: 10000 },
          { name: 'Plan 30GB', price: 20000 },
        ]
      }
      this.showPlanModal = true
    },

    closePlanModal() {
      this.showPlanModal = false
      this.editingPlanProduct = null
      this.editingPlanIndex = null
      this.editingPlans = []
      this.savingPlans = false
    },

    addPlanRow() {
      this.editingPlans.push({
        name: `Plan ${this.editingPlans.length + 1}`,
        price: 0
      })
    },

    removePlanRow(idx) {
      if (this.editingPlans.length <= 1) {
        return alert('Debe haber al menos un plan configurado.')
      }
      this.editingPlans.splice(idx, 1)
    },

    async savePlansConfig() {
      if (!this.editingPlanProduct || !this.editingPlanProduct.id) return
      
      const invalid = this.editingPlans.find(p => !p.name || p.name.trim() === '' || isNaN(p.price) || p.price < 0)
      if (invalid) {
        return alert('Todos los planes deben tener un nombre y un precio en Bs válido (mayor o igual a 0).')
      }

      this.savingPlans = true
      try {
        const response = await axios.post(`/products/${this.editingPlanProduct.id}/plans`, {
          plans: this.editingPlans
        })

        const updatedDesc = response.data.description || JSON.stringify(this.editingPlans)
        
        // 1. Actualizar descripción en el producto de la tabla
        if (this.editingPlanIndex !== null && this.form.products[this.editingPlanIndex]) {
          this.form.products[this.editingPlanIndex].description = updatedDesc
          const newPlans = this.getProductPlans(this.form.products[this.editingPlanIndex])
          const exists = newPlans.find(p => p.name === this.form.products[this.editingPlanIndex].selected_plan)
          if (!exists && newPlans.length > 0) {
            this.form.products[this.editingPlanIndex].selected_plan = newPlans[0].name
          }
          this.onPlanChange(this.editingPlanIndex)
        }

        this.closePlanModal()
        alert('Planes actualizados con éxito.')
      } catch (error) {
        console.error('Error al guardar planes:', error)
        alert('Ocurrió un error al guardar los planes. Por favor intente de nuevo.')
      } finally {
        this.savingPlans = false
      }
    },

    buildProductItem(product) {
      const isPlan = this.isPlanProduct(product.code)
      const isBs = this.isCustomBsProduct(product.code)

      let defaultPrice = product?.prices?.length ? product.prices[0].price : 0
      let defaultGroupId = product?.prices?.length ? product.prices[0].group_id : null
      let priceType = defaultGroupId ? `group-${defaultGroupId}` : 'custom'
      let note = product.note || ''
      let selectedPlan = null
      let selectedPlanPrice = null
      let priceBs = null

      if (isPlan) {
        const plans = this.getProductPlans(product)
        if (plans.length > 0) {
          const firstPlan = plans[0]
          selectedPlan = firstPlan.name
          selectedPlanPrice = firstPlan.price
          priceBs = firstPlan.price
          const rate = (this.form?.change && Number(this.form.change) > 0) ? Number(this.form.change) : (this.change && Number(this.change) > 0 ? Number(this.change) : 1)
          defaultPrice = Number((firstPlan.price / rate).toFixed(4))
          note = firstPlan.name
          priceType = 'plan'
        }
      } else if (isBs) {
        priceType = 'custom'
        defaultPrice = 0
        priceBs = ''
      }

      return {
        ...product,
        stock: product.quantity,
        quantity: 1,
        price: defaultPrice,
        price_bs: priceBs,
        selected_plan: selectedPlan,
        selectedPlanPrice: selectedPlanPrice,
        priceType: priceType,
        note: note,
        require_note: isPlan || isBs ? true : !!product.require_note,
        quick: false
      }
    },

    onPlanChange(index) {
      const item = this.form.products[index]
      if (!item) return

      if (item.selected_plan === '__config__') {
        const plans = this.getProductPlans(item).filter(p => p.name !== 'Personalizado')
        item.selected_plan = plans.length > 0 ? plans[0].name : null
        this.openPlanConfigModal(item, index)
        return
      }

      const plans = this.getProductPlans(item)
      const selected = plans.find(p => p.name === item.selected_plan)
      if (selected) {
        if (selected.name === 'Personalizado') {
          item.price_bs = ''
          item.price = 0
          item.note = 'Recarga Plan'
        } else {
          item.price_bs = selected.price
          item.selectedPlanPrice = selected.price
          const rate = (this.form?.change && Number(this.form.change) > 0) ? Number(this.form.change) : (this.change && Number(this.change) > 0 ? Number(this.change) : 1)
          item.price = Number((selected.price / rate).toFixed(4))
          const planNames = plans.map(p => p.name)
          if (!item.note) {
            item.note = selected.name
          } else {
            const matchedName = planNames.find(n => item.note.startsWith(n))
            if (matchedName) {
              item.note = selected.name + item.note.substring(matchedName.length)
            } else {
              item.note = `${selected.name} - ${item.note}`
            }
          }
        }
      }
    },

    onPriceBsChange(index) {
      const item = this.form.products[index]
      if (!item) return
      const val = parseFloat(item.price_bs)
      if (!isNaN(val) && val > 0) {
        const rate = (this.form?.change && Number(this.form.change) > 0) ? Number(this.form.change) : (this.change && Number(this.change) > 0 ? Number(this.change) : 1)
        item.price = Number((val / rate).toFixed(4))
      } else {
        item.price = 0
      }
    },

    selectProduct(product) {
      const isRecarga = this.isPlanProduct(product.code) || this.isCustomBsProduct(product.code)
      if (!isRecarga && product.quantity <= 0) {
        return alert('No hay stock disponible de este producto')
      }

      const exists = this.form.products.some(p => p.id === product.id)
      
      if (!exists) {
        this.form.products.push(this.buildProductItem(product))
      }
    },

    removeProduct(product) {
      if (confirm('¿Está seguro que desea eliminar este producto?')) {
        this.form.products = this.form.products.filter(p => p.id !== product.id)
      }
    },

    findClient() {
      this.$inertia.get('/orders/create', pickBy(this.form), { preserveState: true })
      this.textSearch = 'Buscando...'

      setTimeout(() => {
        if (this.client) {
          this.textSearch = 'Buscar cliente'
          this.showClientCreate = true
          this.form.client = {
            name: this.client.name,
            email: this.client.email,
            phone: this.client.phone,
            address: this.client.address,
            identification: this.client.identification,
            balance: this.client.balance,
            typeClient: this.client.typeClient,
            companyName: this.client.companyName,
          }
        } else {
          this.textSearch = 'Buscar cliente'
          alert('No se encontró el cliente. Puede crear uno nuevo en el formulario.')
          this.showClientCreate = true
        }
      }, 1500)
    },

    generateOrder() {
      if (this.form.products.length === 0) {
        return alert("Debe agregar productos a la orden");
      }

      const missingBsPrice = this.form.products.find(p => (this.isCustomBsProduct(p.code) || this.isPlanProduct(p.code)) && (!p.price_bs || Number(p.price_bs) <= 0));
      if (missingBsPrice) {
        return alert(`Debe ingresar un monto en Bs válido para "${missingBsPrice.name}".`);
      }
      
      const missingNote = this.form.products.find(p => (p.require_note || this.isCustomBsProduct(p.code) || this.isPlanProduct(p.code)) && (!p.note || p.note.trim() === ''));
      if (missingNote) {
        return alert(`El producto ${missingNote.name} requiere una nota obligatoria.`);
      }

      this.form.post('/orders')
    },

    onSearchProductsChange() {
      this.$inertia.get('/orders/create', 
        pickBy({ search: this.form.search }), 
        {
          preserveState: true,
          preserveScroll: true,
          replace: true,
        }
      )
    },

    onSelectedProduct(consultant) {
      console.log(consultant)
    },

    updateSelected(newSelected) {
      this.selectProduct = newSelected
    },

    onPriceTypeChange(index) {
      const product = this.form.products[index]
      if (!product) return

      if (this.isPlanProduct(product.code) || this.isCustomBsProduct(product.code)) {
        return
      }

      if (product.priceType === 'custom') {
        // For sales role, enforce group price if they somehow end up on custom
        if (this.auth.user.role === 'sales') {
          const fallbackGroupId = product?.prices?.length ? product.prices[0].group_id : null
          product.priceType = fallbackGroupId ? `group-${fallbackGroupId}` : null
        }
        return
      }

      if (product.priceType && product.priceType.startsWith('group-')) {
        const groupId = parseInt(product.priceType.replace('group-', ''))
        const price = this.getGroupPrice(product.prices, groupId)
        if (price !== '-') {
          this.form.products[index].price = price
        }
      }
    },
  },
}
</script>
